<template>
  <div class="pg">
    <div class="pg__head">
      <div>
        <h1 class="page-title">在线调试</h1>
        <!-- <p class="page-desc">
          真实调用 <code class="mono">/idoing_*</code>：OpenGate Key + Secret，积分余额须 &gt; 0。
          <router-link to="/keys">管理密钥</router-link>
          ·
          <router-link :to="{ path: '/docs', query: current ? { id: current.id } : undefined }">看文档</router-link>
        </p> -->
      </div>
      <div class="pg__base mono">Base {{ baseDisplay }}</div>
    </div>

    <div class="pg__layout">
      <!-- 左侧：分类 + 接口 -->
      <aside class="panel pg__nav">
        <input
          v-model="search"
          type="search"
          class="pg__search"
          placeholder="搜索接口 / path…"
        />
        <div class="pg__types">
          <button
            type="button"
            class="pg__type"
            :class="{ 'is-active': typeFilter === 'all' }"
            @click="typeFilter = 'all'"
          >
            全部
          </button>
          <button
            v-for="t in typeTabs"
            :key="t.id"
            type="button"
            class="pg__type"
            :class="{ 'is-active': typeFilter === t.id }"
            @click="typeFilter = t.id"
          >
            {{ t.name }}
          </button>
        </div>
        <div class="pg__ep-list">
          <button
            v-for="ep in filteredEndpoints"
            :key="ep.id"
            type="button"
            class="pg__ep"
            :class="{ 'is-active': endpointId === ep.id }"
            @click="selectEndpoint(ep.id)"
          >
            <span class="badge" :class="'badge-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <span class="pg__ep-meta">
              <span class="pg__ep-title">{{ ep.summary }}</span>
              <span class="pg__ep-path mono">{{ ep.path }}</span>
            </span>
          </button>
          <p v-if="!filteredEndpoints.length" class="pg__empty">无匹配接口</p>
        </div>
      </aside>

      <!-- 中间：请求 -->
      <section class="panel pg__form">
        <template v-if="current">
          <div class="pg__form-head">
            <span class="badge" :class="'badge-' + current.method.toLowerCase()">{{ current.method }}</span>
            <h2 class="pg__form-title">{{ current.summary }}</h2>
          </div>
          <p v-if="current.desc" class="pg__desc">{{ current.desc }}</p>
          <p class="pg__meta mono">{{ current.controller || '—' }}</p>

          <div v-if="scoreBalance != null" class="pg__balance">
            密钥对应用户 #<span class="mono">{{ scoreUserId || '?' }}</span>
            · 可用积分 <strong class="mono">{{ scoreBalance }}</strong>
            <button type="button" class="btn btn-ghost pg__mini" :disabled="balanceLoading" @click="checkBalance">
              {{ balanceLoading ? '查询中…' : '刷新余额' }}
            </button>
            <router-link class="btn btn-ghost pg__mini" to="/recharge">充值</router-link>
          </div>
          <p v-else class="pg__hint" style="margin-bottom: 12px">
            填写 Key/Secret 后可点「刷新余额」。
            <button type="button" class="btn btn-ghost pg__mini" :disabled="balanceLoading" @click="checkBalance">
              {{ balanceLoading ? '查询中…' : '刷新余额' }}
            </button>
            <router-link class="btn btn-ghost pg__mini" to="/recharge">充值积分</router-link>
          </p>

          <div class="pg__auth">
            <div class="field">
              <label>OpenGate API Key</label>
              <select v-if="keyOptions.length" v-model="selectedKeyId">
                <option disabled value="">选择已创建的密钥</option>
                <option v-for="k in keyOptions" :key="k.id" :value="k.id" :disabled="!k.enabled">
                  {{ k.name }} · {{ k.key }}{{ k.enabled ? '' : '（已吊销）' }}
                </option>
              </select>
              <input
                v-model="apiKey"
                type="text"
                class="mono"
                :style="keyOptions.length ? 'margin-top: 8px' : ''"
                placeholder="og_live_...（可选手动填写）"
              />
            </div>
            <div class="field" style="margin-top: 12px">
              <label>API Secret（X-Api-Secret）</label>
              <div class="pg__secret-row">
                <input
                  v-model="apiSecret"
                  :type="showSecret ? 'text' : 'password'"
                  class="mono"
                  placeholder="创建密钥时保存的 sk_..."
                  autocomplete="off"
                />
                <button type="button" class="btn btn-ghost pg__mini" @click="showSecret = !showSecret">
                  {{ showSecret ? '隐藏' : '显示' }}
                </button>
              </div>
              <p class="pg__hint">Secret 仅创建时完整展示一次，请从本地备份粘贴。</p>
            </div>
          </div>

          <div
            v-for="name in pathParamNames"
            :key="'path-' + name"
            class="field"
            style="margin-top: 14px"
          >
            <label>Path · {{ name }}</label>
            <input v-model="pathParamValues[name]" type="text" class="mono" :placeholder="name" />
          </div>

          <div
            v-if="hasBody"
            class="pg__body-mode"
          >
            <div class="pg__tabs">
              <button
                type="button"
                :class="{ 'is-active': bodyMode === 'form' }"
                @click="switchBodyMode('form')"
              >
                表单
              </button>
              <button
                type="button"
                :class="{ 'is-active': bodyMode === 'json' }"
                @click="switchBodyMode('json')"
              >
                JSON
              </button>
            </div>

            <div v-if="bodyMode === 'form'" class="pg__fields">
              <div v-for="f in bodyFields" :key="f.name" class="field">
                <label>
                  {{ f.name }}
                  <span v-if="f.required" class="req">*</span>
                  <span class="pg__ftype mono">{{ f.type }}</span>
                </label>
                <textarea
                  v-if="isComplexField(f)"
                  v-model="formValues[f.name]"
                  rows="3"
                  class="mono"
                  :placeholder="fieldPlaceholder(f)"
                  spellcheck="false"
                />
                <input
                  v-else-if="f.type === 'number'"
                  v-model.number="formValues[f.name]"
                  type="number"
                  :placeholder="fieldPlaceholder(f)"
                />
                <input
                  v-else
                  v-model="formValues[f.name]"
                  type="text"
                  :class="{ mono: f.name.indexOf('url') >= 0 || f.name === 'model' }"
                  :placeholder="fieldPlaceholder(f)"
                />
                <p v-if="f.desc" class="pg__hint">{{ f.desc }}</p>
              </div>
              <p v-if="!bodyFields.length" class="pg__hint">该接口无 body 字段，可切到 JSON 自定义。</p>
            </div>

            <div v-else class="field">
              <label>JSON Body</label>
              <textarea v-model="bodyText" spellcheck="false" class="pg__json" />
            </div>
          </div>

          <p class="pg__url mono">{{ previewUrl }}</p>

          <label v-if="current.pollEndpointId" class="pg__check">
            <input v-model="autoPoll" type="checkbox" />
            创建成功后自动轮询任务（每 {{ pollIntervalSec }}s，最多 {{ maxPolls }} 次）
          </label>

          <div class="pg__actions">
            <button type="button" class="btn btn-primary" :disabled="loading || polling" @click="run">
              {{ loading ? '请求中…' : polling ? '轮询中…' : '发送真实请求' }}
            </button>
            <button type="button" class="btn btn-ghost" :disabled="polling" @click="runMock">仅模拟</button>
            <button type="button" class="btn btn-ghost" @click="resetAll">重置示例</button>
            <button type="button" class="btn btn-ghost" @click="copyCurl">复制 cURL</button>
            <button type="button" class="btn btn-ghost" @click="loadKeys">刷新密钥</button>
            <button
              v-if="polling"
              type="button"
              class="btn btn-danger"
              @click="stopPoll"
            >
              停止轮询
            </button>
          </div>
        </template>
      </section>

      <!-- 右侧：响应 -->
      <section class="panel pg__out">
        <div class="pg__out-head">
          <span>Response</span>
          <div class="pg__out-meta">
            <span v-if="latencyMs != null" class="mono muted">{{ latencyMs }}ms</span>
            <span v-if="statusLabel" class="mono" :class="ok ? 'ok' : 'err'">{{ statusLabel }}</span>
            <button v-if="output" type="button" class="btn btn-ghost pg__mini" @click="copyOutput">复制</button>
          </div>
        </div>

        <div v-if="pollStatus" class="pg__poll">
          <span class="mono">{{ pollStatus }}</span>
          <button
            v-if="lastTaskId && current && current.pollEndpointId"
            type="button"
            class="btn btn-ghost pg__mini"
            @click="goPollEndpoint(lastTaskId)"
          >
            打开查询接口
          </button>
        </div>

        <div v-if="previewImages.length || previewVideos.length" class="pg__media">
          <a
            v-for="(src, i) in previewImages"
            :key="'img-' + i"
            :href="src"
            target="_blank"
            rel="noopener"
            class="pg__media-item"
          >
            <img :src="src" alt="result" />
          </a>
          <video
            v-for="(src, i) in previewVideos"
            :key="'vid-' + i"
            class="pg__media-item"
            :src="src"
            controls
            playsinline
          />
        </div>

        <pre class="mono pg__pre">{{ output || '// 填写 Key + Secret 后发送（需积分余额 > 0）' }}</pre>

        <div v-if="history.length" class="pg__history">
          <div class="pg__history-head">
            <span>最近请求</span>
            <button type="button" class="btn btn-ghost pg__mini" @click="history = []">清空</button>
          </div>
          <button
            v-for="(h, idx) in history"
            :key="h.at + '-' + idx"
            type="button"
            class="pg__history-item"
            @click="restoreHistory(h)"
          >
            <span class="badge" :class="'badge-' + h.method.toLowerCase()">{{ h.method }}</span>
            <span class="mono">{{ h.path }}</span>
            <span :class="h.ok ? 'ok' : 'err'" class="mono">{{ h.status }}</span>
            <span class="muted mono">{{ h.ms }}ms</span>
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import {
  API_BASE,
  allEndpoints,
  findEndpoint,
  productTypes,
  resolveRequestUrl
} from '../data/apiCatalog'
import { fetchApiKeys, pickApiPayload } from '../utils/apiKeysApi'

const HISTORY_KEY = 'opengate_pg_history_v1'
const MAX_HISTORY = 12

export default {
  name: 'Playground',
  data() {
    const list = allEndpoints()
    const qid = this.$route.query.id
    const first = list.find(e => e.id === qid) || list[0]
    return {
      endpoints: list,
      typeTabs: productTypes.filter(t => t.id !== 'all'),
      typeFilter: 'all',
      search: '',
      endpointId: first ? first.id : '',
      keyOptions: [],
      selectedKeyId: '',
      apiKey: localStorage.getItem('opengate_debug_key') || '',
      apiSecret: localStorage.getItem('opengate_debug_secret') || '',
      showSecret: false,
      bodyMode: 'form',
      bodyText: '',
      formValues: {},
      pathParamValues: {},
      output: '',
      statusLabel: '',
      ok: true,
      loading: false,
      latencyMs: null,
      autoPoll: true,
      polling: false,
      pollTimer: null,
      pollStatus: '',
      lastTaskId: '',
      pollIntervalSec: 3,
      maxPolls: 40,
      previewImages: [],
      previewVideos: [],
      history: this.readHistory(),
      scoreBalance: null,
      scoreUserId: null,
      balanceLoading: false
    }
  },
  computed: {
    baseDisplay() {
      return API_BASE || ''
    },
    current() {
      const hit = findEndpoint(this.endpointId)
      return hit ? hit.endpoint : null
    },
    filteredEndpoints() {
      const q = String(this.search || '')
        .trim()
        .toLowerCase()
      return this.endpoints.filter(ep => {
        if (this.typeFilter !== 'all' && ep.groupId !== this.typeFilter) return false
        if (!q) return true
        return (
          ep.summary.toLowerCase().indexOf(q) >= 0 ||
          ep.path.toLowerCase().indexOf(q) >= 0 ||
          ep.id.toLowerCase().indexOf(q) >= 0
        )
      })
    },
    pathParamNames() {
      return (this.current && this.current.pathParams) || []
    },
    hasBody() {
      if (!this.current) return false
      if (this.current.method.toUpperCase() === 'GET') return false
      return this.current.sampleBody !== null && this.current.sampleBody !== undefined
    },
    bodyFields() {
      if (!this.current || !Array.isArray(this.current.params)) return []
      return this.current.params.filter(p => p.in === 'body')
    },
    previewUrl() {
      if (!this.current) return ''
      return resolveRequestUrl(this.current.path, this.pathParamValues)
    }
  },
  watch: {
    endpointId(id) {
      this.applyEndpoint(id)
      if (this.$route.query.id !== id) {
        this.$router.replace({ query: { ...this.$route.query, id } }).catch(() => {})
      }
    },
    selectedKeyId(id) {
      const hit = this.keyOptions.find(k => k.id === id)
      if (hit) this.apiKey = hit.key
    },
    apiKey(v) {
      localStorage.setItem('opengate_debug_key', v || '')
    },
    apiSecret(v) {
      localStorage.setItem('opengate_debug_secret', v || '')
    },
    '$route.query.id'(id) {
      if (id && this.endpoints.some(e => e.id === id) && id !== this.endpointId) {
        this.endpointId = id
      }
    }
  },
  mounted() {
    this.applyEndpoint(this.endpointId)
    this.loadKeys()
  },
  beforeDestroy() {
    this.stopPoll()
  },
  methods: {
    readHistory() {
      try {
        const raw = localStorage.getItem(HISTORY_KEY)
        const list = raw ? JSON.parse(raw) : []
        return Array.isArray(list) ? list : []
      } catch (e) {
        return []
      }
    },
    persistHistory() {
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(this.history.slice(0, MAX_HISTORY)))
      } catch (e) {
        /* ignore */
      }
    },
    selectEndpoint(id) {
      this.stopPoll()
      this.endpointId = id
      this.output = ''
      this.statusLabel = ''
      this.latencyMs = null
      this.pollStatus = ''
      this.previewImages = []
      this.previewVideos = []
    },
    applyEndpoint(id) {
      const hit = findEndpoint(id)
      if (!hit) return
      const ep = hit.endpoint
      this.pathParamValues = Object.assign({}, ep.samplePathParams || {})
      this.bodyText = ep.sampleBody ? JSON.stringify(ep.sampleBody, null, 2) : ''
      this.formValues = this.sampleToForm(ep.sampleBody)
      this.bodyMode = 'form'
      if (ep.groupId) this.typeFilter = ep.groupId
    },
    sampleToForm(sample) {
      const out = {}
      const src = sample && typeof sample === 'object' ? sample : {}
      this.bodyFields.forEach(f => {
        const v = src[f.name]
        if (v === undefined || v === null) {
          out[f.name] = this.isComplexField(f) ? '' : ''
        } else if (typeof v === 'object') {
          out[f.name] = JSON.stringify(v, null, 2)
        } else {
          out[f.name] = v
        }
      })
      // 样本里有但 params 未声明的字段也带上
      Object.keys(src).forEach(k => {
        if (out[k] === undefined) {
          const v = src[k]
          out[k] = typeof v === 'object' ? JSON.stringify(v, null, 2) : v
        }
      })
      return out
    },
    isComplexField(f) {
      const t = String((f && f.type) || '').toLowerCase()
      return t.indexOf('array') >= 0 || t.indexOf('object') >= 0 || t.indexOf('|') >= 0
    },
    fieldPlaceholder(f) {
      if (this.isComplexField(f)) return 'JSON，如 ["https://..."]'
      return f.desc || f.name
    },
    switchBodyMode(mode) {
      if (mode === this.bodyMode) return
      if (mode === 'json') {
        try {
          this.bodyText = JSON.stringify(this.buildBodyFromForm(), null, 2)
        } catch (e) {
          this.bodyText = this.bodyText || '{}'
        }
      } else {
        try {
          const parsed = JSON.parse(this.bodyText || '{}')
          this.formValues = this.sampleToForm(parsed)
        } catch (e) {
          this.flashHint('JSON 无效，已保留原表单')
        }
      }
      this.bodyMode = mode
    },
    buildBodyFromForm() {
      const out = {}
      const keys = new Set([
        ...this.bodyFields.map(f => f.name),
        ...Object.keys(this.formValues || {})
      ])
      keys.forEach(name => {
        const f = this.bodyFields.find(x => x.name === name) || { name, type: 'string' }
        let raw = this.formValues[name]
        if (raw === '' || raw === undefined || raw === null) return
        if (this.isComplexField(f) || (typeof raw === 'string' && /^\s*[\[{]/.test(raw))) {
          try {
            out[name] = JSON.parse(raw)
          } catch (e) {
            throw new Error(`字段 ${name} 不是合法 JSON`)
          }
        } else if (f.type === 'number') {
          const n = Number(raw)
          if (!Number.isNaN(n)) out[name] = n
        } else {
          out[name] = raw
        }
      })
      return out
    },
    resetAll() {
      this.applyEndpoint(this.endpointId)
      this.pollStatus = ''
      this.lastTaskId = ''
    },
    async loadKeys() {
      try {
        const res = await fetchApiKeys()
        const data = pickApiPayload(res) || {}
        this.keyOptions = Array.isArray(data.list) ? data.list : []
        if (!this.selectedKeyId && this.keyOptions.length) {
          const en = this.keyOptions.find(k => k.enabled) || this.keyOptions[0]
          this.selectedKeyId = en.id
          this.apiKey = en.key
        }
      } catch (e) {
        this.keyOptions = []
      }
    },
    async checkBalance() {
      const auth = this.validateAuth()
      if (!auth.ok) {
        this.flashHint(auth.message)
        return
      }
      this.balanceLoading = true
      try {
        const res = await axios({
          method: 'GET',
          url: resolveRequestUrl('/idoing_products', {}),
          headers: {
            Accept: 'application/json',
            Authorization: auth.key,
            'X-Api-Secret': auth.secret
          },
          validateStatus: () => true,
          timeout: 30000
        })
        if (res.status < 200 || res.status >= 300) {
          const msg =
            (res.data && (res.data.message || res.data.msg)) ||
            `HTTP ${res.status}`
          this.scoreBalance = null
          this.flashHint(String(msg))
          this.ok = false
          this.statusLabel = String(res.status)
          this.output = JSON.stringify(res.data, null, 2)
          return
        }
        // 兼容多层包装：{code,data:{score_balance}} / {data:{data:{...}}} / 顶层字段
        const body = res.data != null ? res.data : {}
        let data =
          body.data && typeof body.data === 'object' ? body.data : body
        if (
          data &&
          data.data &&
          typeof data.data === 'object' &&
          data.score_balance == null &&
          data.data.score_balance != null
        ) {
          data = data.data
        }
        this.output = JSON.stringify(body, null, 2)
        this.scoreBalance =
          data.score_balance != null
            ? Number(data.score_balance)
            : data.score != null
              ? Number(data.score)
              : null
        this.scoreUserId =
          data.user_id != null
            ? data.user_id
            : data.id != null
              ? data.id
              : null
        this.ok = this.scoreBalance != null && this.scoreBalance > 0
        this.statusLabel = String(res.status)
        if (this.scoreBalance != null) {
          this.flashHint(
            `可用积分 ${this.scoreBalance}（用户#${this.scoreUserId}，来源 ${(data.score_debug && data.score_debug.source) || '?'}）`
          )
        } else {
          const keys = data && typeof data === 'object' ? Object.keys(data).join(',') : ''
          this.flashHint(
            `已连通，但响应无 score_balance（字段: ${keys || '无'}）。请把仓库 IdoingProductController.php 覆盖到服务器 Modules/Activity/Http/Controllers/Client/Movie/ 并清 PHP/OPcache`
          )
        }
      } catch (e) {
        this.flashHint(e.message || '查询失败')
      } finally {
        this.balanceLoading = false
      }
    },
    validateAuth() {
      const key = String(this.apiKey || '').trim()
      const secret = String(this.apiSecret || '').trim()
      if (!key || key.indexOf('og_live_') !== 0) {
        return { ok: false, message: '请填写有效的 OpenGate API Key（og_live_...），在「密钥」页创建' }
      }
      if (!secret) {
        return { ok: false, message: '请填写 API Secret（创建密钥时保存的 sk_...）' }
      }
      return { ok: true, key, secret }
    },
    parseRequestBody() {
      if (!this.hasBody) return undefined
      if (this.bodyMode === 'json') {
        return JSON.parse(this.bodyText || '{}')
      }
      return this.buildBodyFromForm()
    },
    extractMedia(data) {
      const images = []
      const videos = []
      if (!data || typeof data !== 'object') {
        this.previewImages = []
        this.previewVideos = []
        return
      }
      const pushImg = u => {
        if (u && typeof u === 'string' && /^https?:\/\//i.test(u) && images.indexOf(u) < 0) images.push(u)
      }
      const pushVid = u => {
        if (u && typeof u === 'string' && /^https?:\/\//i.test(u) && videos.indexOf(u) < 0) videos.push(u)
      }
      pushImg(data.url)
      pushVid(data.video_url)
      if (Array.isArray(data.urls)) {
        data.urls.forEach(item => {
          if (typeof item === 'string') pushImg(item)
          else if (item && item.url) pushImg(item.url)
        })
      }
      this.previewImages = images
      this.previewVideos = videos
    },
    extractTaskId(data) {
      if (!data || typeof data !== 'object') return ''
      return String(data.task_id || data.id || '').trim()
    },
    isTerminalStatus(status) {
      const s = String(status || '').toLowerCase()
      return (
        s === 'succeeded' ||
        s === 'success' ||
        s === 'completed' ||
        s === 'failed' ||
        s === 'error' ||
        s === 'canceled' ||
        s === 'cancelled'
      )
    },
    pushHistory(entry) {
      this.history = [entry, ...this.history.filter(h => h.at !== entry.at)].slice(0, MAX_HISTORY)
      this.persistHistory()
    },
    restoreHistory(h) {
      if (!h) return
      this.selectEndpoint(h.endpointId)
      this.$nextTick(() => {
        if (h.pathParams) this.pathParamValues = Object.assign({}, h.pathParams)
        if (h.body != null) {
          this.bodyMode = 'json'
          this.bodyText = JSON.stringify(h.body, null, 2)
          this.formValues = this.sampleToForm(h.body)
        }
        this.output = h.responseText || ''
        this.statusLabel = String(h.status || '')
        this.ok = !!h.ok
        this.latencyMs = h.ms
        try {
          this.extractMedia(JSON.parse(h.responseText || 'null'))
        } catch (e) {
          this.previewImages = []
          this.previewVideos = []
        }
      })
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        return true
      } catch (e) {
        const ta = document.createElement('textarea')
        ta.value = text
        document.body.appendChild(ta)
        ta.select()
        try {
          document.execCommand('copy')
          return true
        } finally {
          document.body.removeChild(ta)
        }
      }
    },
    async copyOutput() {
      if (!this.output) return
      const ok = await this.copyText(this.output)
      if (ok) this.flashHint('已复制响应')
    },
    async copyCurl() {
      if (!this.current) return
      let body
      try {
        body = this.parseRequestBody()
      } catch (e) {
        this.ok = false
        this.statusLabel = '400'
        this.output = JSON.stringify({ message: e.message || 'Body 无效' }, null, 2)
        return
      }
      const key = String(this.apiKey || '').trim() || 'og_live_YOUR_KEY'
      const secret = String(this.apiSecret || '').trim() || 'sk_YOUR_SECRET'
      const url = this.previewUrl
      const lines = [
        `curl -X ${this.current.method.toUpperCase()} '${url}' \\`,
        `  -H 'Authorization: ${key}' \\`,
        `  -H 'X-Api-Secret: ${secret}' \\`,
        `  -H 'Content-Type: application/json' \\`,
        `  -H 'Accept: application/json'`
      ]
      if (body !== undefined) {
        lines[lines.length - 1] += ' \\'
        lines.push(`  -d '${JSON.stringify(body)}'`)
      }
      const ok = await this.copyText(lines.join('\n'))
      if (ok) this.flashHint('已复制 cURL')
    },
    flashHint(msg) {
      this.pollStatus = msg
      setTimeout(() => {
        if (this.pollStatus === msg) this.pollStatus = ''
      }, 1600)
    },
    runMock() {
      if (!this.current) return
      this.stopPoll()
      this.ok = true
      this.statusLabel = '200 OK · mock'
      this.latencyMs = 0
      this.output = JSON.stringify(this.current.sampleResponse, null, 2)
      this.extractMedia(this.current.sampleResponse)
      this.pollStatus = '模拟响应（未真实请求）'
    },
    goPollEndpoint(taskId) {
      if (!this.current || !this.current.pollEndpointId) return
      const id = this.current.pollEndpointId
      this.selectEndpoint(id)
      this.$nextTick(() => {
        this.$set(this.pathParamValues, 'task_id', taskId)
      })
    },
    stopPoll() {
      this.polling = false
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    schedulePoll(pollId, taskId, attempt) {
      this.stopPoll()
      this.polling = true
      this.pollStatus = `轮询中 ${attempt}/${this.maxPolls} · task_id=${taskId}`
      this.pollTimer = setTimeout(async () => {
        const hit = findEndpoint(pollId)
        if (!hit) {
          this.polling = false
          return
        }
        const ep = hit.endpoint
        const auth = this.validateAuth()
        if (!auth.ok) {
          this.polling = false
          this.pollStatus = auth.message
          return
        }
        const url = resolveRequestUrl(ep.path, { task_id: taskId })
        const t0 = Date.now()
        try {
          const res = await axios({
            method: 'GET',
            url,
            headers: {
              Accept: 'application/json',
              Authorization: auth.key,
              'X-Api-Secret': auth.secret
            },
            validateStatus: () => true
          })
          this.latencyMs = Date.now() - t0
          this.ok = res.status >= 200 && res.status < 300
          this.statusLabel = `${res.status} · poll #${attempt}`
          this.output = JSON.stringify(res.data, null, 2)
          this.extractMedia(res.data)
          const status = res.data && res.data.status
          if (this.ok && this.isTerminalStatus(status)) {
            this.polling = false
            this.pollStatus = `轮询结束：${status}`
            return
          }
          if (attempt >= this.maxPolls) {
            this.polling = false
            this.pollStatus = '已达最大轮询次数，可手动打开查询接口'
            return
          }
          this.schedulePoll(pollId, taskId, attempt + 1)
        } catch (e) {
          this.polling = false
          this.ok = false
          this.statusLabel = 'ERR'
          this.pollStatus = e.message || '轮询失败'
        }
      }, this.pollIntervalSec * 1000)
    },
    async run() {
      if (!this.current) return
      this.stopPoll()
      this.previewImages = []
      this.previewVideos = []

      const auth = this.validateAuth()
      if (!auth.ok) {
        this.ok = false
        this.statusLabel = '401'
        this.output = JSON.stringify({ message: auth.message }, null, 2)
        return
      }

      let parsed
      try {
        parsed = this.parseRequestBody()
      } catch (e) {
        this.ok = false
        this.statusLabel = '400'
        this.output = JSON.stringify({ message: e.message || 'Body 不是合法 JSON' }, null, 2)
        return
      }

      for (const name of this.pathParamNames) {
        if (!String(this.pathParamValues[name] || '').trim()) {
          this.ok = false
          this.statusLabel = '400'
          this.output = JSON.stringify({ message: `请填写 Path 参数 ${name}` }, null, 2)
          return
        }
      }

      const url = resolveRequestUrl(this.current.path, this.pathParamValues)
      this.loading = true
      this.pollStatus = ''
      const t0 = Date.now()
      try {
        const res = await axios({
          method: this.current.method,
          url,
          data: this.current.method.toUpperCase() === 'GET' ? undefined : parsed,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: auth.key,
            'X-Api-Secret': auth.secret
          },
          validateStatus: () => true,
          timeout: 120000
        })
        this.latencyMs = Date.now() - t0
        this.ok = res.status >= 200 && res.status < 300
        this.statusLabel = `${res.status}`
        this.output = JSON.stringify(res.data, null, 2)
        this.extractMedia(res.data)

        const taskId = this.extractTaskId(res.data)
        this.lastTaskId = taskId
        this.pushHistory({
          at: Date.now(),
          endpointId: this.current.id,
          method: this.current.method,
          path: this.current.path,
          pathParams: Object.assign({}, this.pathParamValues),
          body: parsed,
          status: res.status,
          ok: this.ok,
          ms: this.latencyMs,
          responseText: this.output
        })

        if (this.ok && taskId && this.current.pollEndpointId && this.autoPoll) {
          this.pollStatus = `已创建 task_id=${taskId}，开始自动轮询…`
          this.schedulePoll(this.current.pollEndpointId, taskId, 1)
        } else if (this.ok && taskId && this.current.pollEndpointId) {
          this.pollStatus = `task_id=${taskId}（已关闭自动轮询）`
        }
      } catch (e) {
        this.latencyMs = Date.now() - t0
        this.ok = false
        this.statusLabel = 'ERR'
        this.output = JSON.stringify(
          {
            message: e.message || '请求失败',
            hint: '确认已部署 IdoingProductController、账号有积分，且 Key/Secret 正确'
          },
          null,
          2
        )
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.pg {
  max-width: 1280px;
  margin: 0 auto;
}
.pg__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}
.pg__base {
  margin-top: 8px;
  font-size: 12px;
  color: var(--muted);
  padding: 6px 10px;
  border: 1px solid var(--line);
  border-radius: 999px;
}
.page-desc a {
  color: var(--accent);
}
.pg__layout {
  display: grid;
  gap: 14px;
}
@media (min-width: 1100px) {
  .pg__layout {
    grid-template-columns: 240px minmax(0, 1.1fr) minmax(0, 1fr);
    align-items: start;
  }
}
@media (min-width: 760px) and (max-width: 1099px) {
  .pg__layout {
    grid-template-columns: 1fr 1fr;
  }
  .pg__nav {
    grid-column: 1 / -1;
  }
}
.pg__nav {
  padding: 12px;
  max-height: min(78vh, 860px);
  overflow: auto;
}
.pg__search {
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 8px 10px;
  background: var(--bg);
  margin-bottom: 10px;
}
.pg__types {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.pg__type {
  border: 1px solid var(--line);
  background: transparent;
  color: var(--muted);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
}
.pg__type.is-active {
  color: var(--accent);
  border-color: rgba(125, 255, 179, 0.35);
  background: var(--accent-dim);
}
.pg__ep-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pg__ep {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 10px;
  padding: 8px;
  cursor: pointer;
  color: inherit;
}
.pg__ep:hover {
  background: rgba(125, 255, 179, 0.06);
}
.pg__ep.is-active {
  border-color: rgba(125, 255, 179, 0.28);
  background: var(--accent-dim);
}
.pg__ep-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.pg__ep-title {
  font-size: 13px;
  font-weight: 600;
}
.pg__ep-path {
  font-size: 11px;
  color: var(--muted);
  word-break: break-all;
}
.pg__empty {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--muted);
}
.pg__form-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.pg__form-title {
  margin: 0;
  font-size: 18px;
  font-family: var(--font-display);
}
.pg__desc {
  margin: 0 0 8px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.5;
}
.pg__meta {
  margin: 0 0 16px;
  font-size: 12px;
  color: var(--muted);
}
.pg__meta a {
  color: var(--accent);
}
.pg__balance {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(125, 255, 179, 0.28);
  background: var(--accent-dim);
  font-size: 13px;
}
.pg__balance strong {
  color: var(--accent);
  font-size: 16px;
}
.field label {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 6px;
}
.field .req {
  color: var(--danger);
}
.pg__ftype {
  font-size: 11px;
  opacity: 0.7;
}
.field select,
.field input,
.field textarea,
.pg__secret-row input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  background: var(--bg);
  color: var(--text);
}
.pg__json {
  min-height: 220px;
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1.5;
}
.pg__secret-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.pg__hint {
  margin: 6px 0 0;
  font-size: 11px;
  color: var(--muted);
  line-height: 1.4;
}
.pg__body-mode {
  margin-top: 16px;
}
.pg__tabs {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  border: 1px solid var(--line);
  margin-bottom: 12px;
}
.pg__tabs button {
  border: 0;
  background: transparent;
  color: var(--muted);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
}
.pg__tabs button.is-active {
  background: var(--accent-dim);
  color: var(--accent);
}
.pg__fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.pg__url {
  margin: 14px 0 0;
  font-size: 12px;
  color: var(--accent);
  word-break: break-all;
}
.pg__check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 13px;
  color: var(--muted);
  cursor: pointer;
}
.pg__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}
.pg__mini {
  padding: 6px 12px;
  font-size: 12px;
}
.pg__out-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--muted);
}
.pg__out-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pg__poll {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;
  color: var(--warn);
}
.pg__media {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.pg__media-item {
  display: block;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--line);
  background: #000;
  aspect-ratio: 1;
}
.pg__media-item img,
.pg__media-item video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pg__pre {
  margin: 0;
  padding: 12px;
  background: #0a0e0c;
  color: #e2e8f0;
  border-radius: 10px;
  border: 1px solid var(--line);
  overflow: auto;
  min-height: 260px;
  max-height: 52vh;
  font-size: 12px;
  line-height: 1.5;
}
.pg__history {
  margin-top: 14px;
  border-top: 1px solid var(--line);
  padding-top: 12px;
}
.pg__history-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  color: var(--muted);
}
.pg__history-item {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 8px;
  align-items: center;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  color: inherit;
  padding: 6px 4px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
}
.pg__history-item:hover {
  background: rgba(125, 255, 179, 0.06);
}
.muted {
  color: var(--muted);
}
.ok {
  color: #34d399;
}
.err {
  color: var(--danger);
}
</style>
